import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Users, Package, ShoppingCart, TrendingUp, UserCheck, Link2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module to permission mapping for stat cards
const MODULE_PERMISSIONS = {
  partners: 'partners:view',
  clients: 'clients:view',
  orders: 'orders:view',
  revenue: 'revenue:view',
  products: 'products:view'
};

// Role display names
const ROLE_LABELS = {
  admin: 'Admin Dashboard',
  level_1_support: 'Level 1 Dashboard',
  level_2_support: 'Level 2 Dashboard',
  level_3_support: 'Level 3 Dashboard'
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rbacData, setRbacData] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch both stats and RBAC permissions in parallel
      const [statsResponse, rbacResponse] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/rbac/my-permissions`)
      ]);
      setStats(statsResponse.data);
      setRbacData(rbacResponse.data);
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (path) => {
    navigate(path);
  };

  // Check if user has access to a specific module
  const hasModuleAccess = (module) => {
    if (!rbacData) return true; // Default to showing if no RBAC data
    if (rbacData.role === 'admin') return true; // Admin has access to everything
    
    const permission = MODULE_PERMISSIONS[module];
    if (!permission) return true;
    
    return rbacData.permissions?.includes(permission);
  };

  // Check if user has access to a specific module in the RBAC modules list
  const isModuleVisible = (module) => {
    if (!rbacData) return true;
    if (rbacData.role === 'admin') return true;
    return rbacData.modules?.includes(module);
  };

  // Get dashboard title based on role
  const getDashboardTitle = () => {
    if (!user || !user.role) return 'Dashboard';
    return ROLE_LABELS[user.role] || 'Dashboard';
  };

  // Get subtitle based on role
  const getSubtitle = () => {
    if (!user || !user.role) return 'Manage your workspace';
    if (user.role === 'admin') {
      return 'Manage your reseller network and products';
    }
    return 'Shared workspace view - Access based on your permissions';
  };

  // Get welcome message based on role
  const getWelcomeMessage = () => {
    if (!user || !user.role) return 'Welcome to CloudSetu Pvt Ltd';
    if (user.role === 'admin') {
      return 'You have full access to manage products, resellers, and system settings.';
    }
    if (user.role === 'level_3_support') {
      return 'You have advanced operational access to manage products, partners, clients, and orders.';
    }
    if (user.role === 'level_2_support') {
      return 'You have standard support access to manage clients, orders, and support tickets.';
    }
    if (user.role === 'level_1_support') {
      return 'You have basic view access. You can view records and respond to support tickets.';
    }
    return 'Welcome to CloudSetu Pvt Ltd';
  };

  // Build stat cards based on permissions
  const statCards = stats ? [
    {
      title: 'Total Resellers',
      value: stats.total_resellers,
      icon: Users,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-500',
      path: '/admin/resellers',
      module: 'partners',
      testId: 'resellers'
    },
    {
      title: 'Sub-Resellers',
      value: stats.total_sub_resellers,
      icon: Link2,
      iconBg: 'bg-green-50',
      iconColor: 'text-green-500',
      path: '/admin/resellers',
      module: 'partners',
      testId: 'sub-resellers'
    },
    {
      title: 'Total Clients',
      value: stats.total_clients,
      icon: UserCheck,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-500',
      path: '/admin/clients',
      module: 'clients',
      testId: 'clients'
    },
    {
      title: 'Active Orders',
      value: `${stats.active_orders}/${stats.total_orders}`,
      icon: ShoppingCart,
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-500',
      path: '/admin/orders',
      module: 'orders',
      testId: 'orders'
    }
  ].filter(card => hasModuleAccess(card.module) && isModuleVisible(card.module)) : [];

  // Build feature cards based on permissions
  const allFeatureCards = [
    {
      title: 'Product Management',
      description: 'Add, edit, or remove hosting products and set base prices.',
      icon: Package,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-500',
      path: '/admin/products',
      module: 'products'
    },
    {
      title: 'Reseller Network',
      description: 'Monitor and manage your reseller hierarchy.',
      icon: Link2,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-500',
      path: '/admin/resellers',
      module: 'partners'
    },
    {
      title: 'Revenue Tracking',
      description: 'Track orders, commissions, and financial metrics.',
      icon: TrendingUp,
      iconBg: 'bg-violet-50',
      iconColor: 'text-violet-500',
      path: '/admin/revenue',
      module: 'revenue'
    }
  ];

  // Filter feature cards based on module visibility
  const featureCards = allFeatureCards.filter(card => isModuleVisible(card.module));

  return (
    <DashboardLayout role="admin">
      <div className="space-y-6 sm:space-y-8">
        {/* Header - Responsive */}
        <div className="min-w-0 mb-2">
          <div className="flex items-start justify-between">
            <div>
              <h1 
                className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight" 
                data-testid="admin-dashboard-title"
              >
                {getDashboardTitle()}
              </h1>
              <p className="text-slate-500 mt-1 text-sm sm:text-base">{getSubtitle()}</p>
            </div>
            <button onClick={fetchData} className="p-2 rounded-lg hover:bg-slate-100 border border-slate-200 mt-1" title="Refresh" data-testid="refresh-btn"><RefreshCw className="h-4 w-4 text-slate-500" /></button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48 sm:h-64" role="status" aria-label="Loading dashboard">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Stats Cards - Responsive Grid */}
            {statCards.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-5 mb-5">
                {statCards.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <Card 
                      key={index} 
                      className="bg-white border-0 shadow-sm hover:shadow-md transition-all cursor-pointer group"
                      data-testid={`stat-card-${stat.testId}`}
                      onClick={() => handleCardClick(stat.path)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => e.key === 'Enter' && handleCardClick(stat.path)}
                      aria-label={`${stat.title}: ${stat.value}. Click to view details`}
                    >
                      <CardContent className="p-3 sm:p-4 lg:p-5">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1 sm:space-y-2 lg:space-y-3 min-w-0 flex-1">
                            <p className="text-xs sm:text-sm font-medium text-slate-500 truncate">{stat.title}</p>
                            <p 
                              className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-800 truncate" 
                              data-testid={`stat-value-${stat.testId}`}
                            >
                              {stat.value}
                            </p>
                            <p className="text-[10px] sm:text-xs text-slate-400 group-hover:text-blue-500 transition-colors hidden sm:block">
                              Click to view details
                            </p>
                          </div>
                          <div className={`${stat.iconBg} p-2 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0 ml-2`}>
                            <Icon className={`h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 ${stat.iconColor}`} aria-hidden="true" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Welcome Section */}
            <div className="space-y-4 sm:space-y-6">
              <div className="mb-2">
                <h2 className="text-lg sm:text-xl font-semibold text-slate-800">
                  Welcome to CloudSetu Pvt Ltd Admin Panel
                </h2>
                <p className="text-slate-500 mt-1 text-sm sm:text-base break-words whitespace-normal">
                  {getWelcomeMessage()}
                </p>
              </div>

              {/* Feature Cards - Responsive Grid (only show if user has access to at least one) */}
              {featureCards.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 lg:gap-5 mb-5">
                  {featureCards.map((card, index) => {
                    const Icon = card.icon;
                    return (
                      <Card 
                        key={index}
                        className="bg-white border-0 shadow-sm hover:shadow-md transition-all cursor-pointer group h-auto"
                        onClick={() => handleCardClick(card.path)}
                        data-testid={`feature-card-${card.module}`}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => e.key === 'Enter' && handleCardClick(card.path)}
                        aria-label={`${card.title}: ${card.description}`}
                      >
                        <CardContent className="p-4 sm:p-5 lg:p-6">
                          <div className={`${card.iconBg} w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4`}>
                            <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${card.iconColor}`} aria-hidden="true" />
                          </div>
                          <h3 className="font-semibold text-slate-800 mb-1 sm:mb-2 group-hover:text-blue-600 transition-colors text-sm sm:text-base">
                            {card.title}
                          </h3>
                          <p className="text-xs sm:text-sm text-slate-500 break-words whitespace-normal leading-relaxed">
                            {card.description}
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
